import { useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

const SEO = ({ title, description, keywords, ogTitle, ogDescription, ogImage, ogUrl, canonical }) => {
    const { lang } = useLanguage();

    useEffect(() => {
        // Update Title - Premium Branding
        if (title) {
            // If the title already contains the brand name, don't append suffix
            document.title = (title.includes('Go Nova') || title.includes('404')) 
                ? title 
                : `${title} | Go Nova Media`;
        }

        // Update HTML lang attribute
        document.documentElement.lang = lang;

        // Multilingual Meta Logic
        const domain = 'https://gonova.media';
        const path = window.location.pathname;
        const defaultDescription = lang === 'tr' 
            ? 'Go Nova Medya, TV dizileri, filmler ve içerik üreticileri için geliri optimize eden global bir MCN ve VOD/Dijital Dağıtım şirketidir.' 
            : 'Go Nova Media is a global MCN and Digital Distribution provider optimizing revenue for TV series, movies, and creators.';

        // Meta Description
        let metaDescription = document.querySelector('meta[name="description"]');
        if (!metaDescription) {
            metaDescription = document.createElement('meta');
            metaDescription.name = 'description';
            document.head.appendChild(metaDescription);
        }
        metaDescription.setAttribute('content', description || defaultDescription);

        // Canonical Tag
        let linkCanonical = document.querySelector('link[rel="canonical"]');
        if (!linkCanonical) {
            linkCanonical = document.createElement('link');
            linkCanonical.rel = 'canonical';
            document.head.appendChild(linkCanonical);
        }
        linkCanonical.setAttribute('href', canonical || `${domain}${path}`);

        // Hreflang Tags - Critical for GEO/SEO Authority
        // ----------------------------------------------------
        // Logic: Alternate En <-> Tr link discovery for bots
        const getHreflangs = () => {
            // Strict path resolution to prevent false-positives
            const isTr = path.startsWith('/tr');
            const cleanPath = isTr ? (path.substring(3) || '/') : path;
            const normalizedPath = cleanPath === '/' ? '' : cleanPath;
            
            return [
                { rel: 'alternate', hreflang: 'en', href: `${domain}${normalizedPath}` },
                { rel: 'alternate', hreflang: 'tr', href: `${domain}/tr${normalizedPath}` },
                { rel: 'alternate', hreflang: 'x-default', href: `${domain}${normalizedPath}` }
            ];
        };

        const hreflangs = getHreflangs();
        // Remove old hreflangs to prevent pollution
        document.querySelectorAll('link[hreflang]').forEach(el => el.remove());
        
        hreflangs.forEach(tag => {
            const link = document.createElement('link');
            link.rel = tag.rel;
            link.hreflang = tag.hreflang;
            link.href = tag.href;
            document.head.appendChild(link);
        });

        // Open Graph Tags
        const ogTags = [
            { property: 'og:title', content: ogTitle || title },
            { property: 'og:description', content: ogDescription || description || defaultDescription },
            { property: 'og:image', content: ogImage || 'https://gonova.media/brand/og-image.jpg' }, 
            { property: 'og:url', content: ogUrl || `${domain}${path}` },
            { property: 'og:type', content: 'website' }
        ];

        ogTags.forEach(tag => {
            let meta = document.querySelector(`meta[property="${tag.property}"]`);
            if (!meta) {
                meta = document.createElement('meta');
                meta.setAttribute('property', tag.property);
                document.head.appendChild(meta);
            }
            meta.setAttribute('content', tag.content);
        });

        // Twitter Card Tags
        const twitterTags = [
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: ogTitle || title },
            { name: 'twitter:description', content: ogDescription || description || defaultDescription },
            { name: 'twitter:image', content: ogImage || 'https://gonova.media/brand/og-image.jpg' }
        ];

        twitterTags.forEach(tag => {
            let meta = document.querySelector(`meta[name="${tag.name}"]`);
            if (!meta) {
                meta = document.createElement('meta');
                meta.name = tag.name;
                document.head.appendChild(meta);
            }
            meta.setAttribute('content', tag.content);
        });

    }, [title, description, ogTitle, ogDescription, ogImage, ogUrl, canonical, lang]);

    return null;
};

export default SEO;
